import Squad from "@skrice.studio/mavia-common/build/models/main/Squad";
import UserPurchase from "@skrice.studio/mavia-common/build/models/main/UserPurchase";
import { Grid, Paper, Skeleton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { withStyles } from "@mui/styles";
import axios from "axios";
import * as React from "react";
import { NavLink } from "react-router-dom";
import { Avatar } from "../../components/Avatar";
import { CoatOfArms } from "../../components/CoatOfArms";
import { Config } from "../../config/config";
import { getGlobalVar, Helpers } from "../../utils/helpers";
import { DashboardTopAlliances } from "./items/topAlliances";
import { DashboardTopUsers } from "./items/topUsers";

interface DashboardWrappedState {
    stats: {
        totalUsers: string | number;
        usersWithShield: number;
        totalSquads: string | number;
        vipUsers: string | number;
        bannedUsers: string | number;
        joinedAlliance: string | number;
        wagerPlayers: string | number;
    },
    tables: {
        latestPayments: UserPurchase[];
    }

    statsLoaded: boolean;
    tablesLoaded: boolean;
}

interface DashboardWrappedProps {
    activeUser: any;
    activeEnv: any;
    classes: any;
}

class DashboardWrapped extends React.Component<DashboardWrappedProps, DashboardWrappedState> {
    constructor(props) {
        super(props);

        this.state = {
            statsLoaded: false,
            tablesLoaded: false,
            stats: {
                totalUsers: 0,
                totalSquads: 0,
                usersWithShield: 0,
                vipUsers: 0,
                bannedUsers: 0,
                joinedAlliance: 0,
                wagerPlayers: 0
            },
            tables: {
                latestPayments: []
            }
        };

        document.title = Helpers.documentTitle("Dashboard");
    }

    componentDidMount() {
        this.loadTables();
        this.loadStats();
    }

    async loadTables() {
        const response = await axios.get(`${Config.getInstance.apiUrl}/api/dashboard/tables`, {
            headers: {
                Authorization: Helpers.getSessionId(),
            },
        });

        this.setState({
            tables: response.data.data.tables,
            tablesLoaded: true,
        });
    }

    async loadStats() {
        const response = await axios.get(`${Config.getInstance.apiUrl}/api/dashboard/stats`, {
            headers: {
                Authorization: Helpers.getSessionId(),
            },
        });

        console.log("response", response.data.data.stats)

        this.setState({
            stats: response.data.data.stats,
            statsLoaded: true,
        });
    }

    getGoldPackData = (id, field) => {
        const goldPackData = getGlobalVar("PRODUCT_LIST").find(item => item.productId === id);

        if (!goldPackData) {
            if (field === "price") {
                return 0;
            }

            return "Unknown product";
        }

        if (field === "price") {
            return goldPackData ? goldPackData[field] : 0;
        }

        if (field === "name") {
            return goldPackData ? id : "Didn't pass validation";
        }

        return goldPackData ? goldPackData[field] : id;
    };

    formatUserTableBody = (items: any[]) => {
        if (!items) {
            return [];
        }

        const {classes} = this.props;

        return items.map(item => (
            <TableRow
                key={item.id}
                hover={true}
                className={classes.tableRow}
            >
                <TableCell>{Avatar(item)}</TableCell>
                <TableCell>
                    <NavLink to={`/players/${item.id}`}>
                        {item.name}
                    </NavLink>
                </TableCell>
                <TableCell>{item.headquarter_level}</TableCell>
                <TableCell>{item.vip_system.level}</TableCell>
                <TableCell>{item.rubies}</TableCell>
                <TableCell>{item.country}</TableCell>
                <TableCell>{item.is_online ? "Online" : "Offline"}</TableCell>
            </TableRow>
        ));
    };

    formatAllianceTableBody = (items: Squad[]) => {
        if (!items) {
            return [];
        }

        const {classes} = this.props;

        return items.map(item => (
            <TableRow
                key={item.id}
                hover={true}
                className={classes.tableRow}
            >
                <TableCell><CoatOfArms emblem={item.flag}/></TableCell>
                <TableCell>
                    <NavLink to={`/alliances/${item.id}`}>
                        {item.name}
                    </NavLink>
                </TableCell>
                <TableCell>{item.tag}</TableCell>
                <TableCell>{item.members_count}/{item.max_members}</TableCell>
                <TableCell>{item.current_rank}</TableCell>
                <TableCell>{new Date(item.created_at).toLocaleString()}</TableCell>
            </TableRow>
        ));
    };

    render() {
        const {classes} = this.props;
        const showAlliances = Config.getInstance.getSettingByKey("SHOW_DASHBOARD_ALLIANCE");

        return (
            <div className="contentPadding">
                <Grid container spacing={3}>
                    <Grid item xs={4}>
                        <Paper className="stats-root" elevation={0}>
                            <div style={{float: "left", padding: "15px 22px 15px 22px", width: "100%"}}>
                                <div className={classes.title}>
                                    <Typography variant="h6" style={{textAlign: "center"}}>
                                        Total Users
                                    </Typography>

                                    <Typography variant="h2" style={{textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center"}}>
                                        {this.state.statsLoaded ? this.state.stats.totalUsers : <Skeleton width={150}/>}
                                    </Typography>
                                </div>
                            </div>
                        </Paper>
                    </Grid>

                    <Grid item xs={4}>
                        <Paper className="stats-root" elevation={0}>
                            <div style={{float: "left", padding: "15px 22px 15px 22px", width: "100%"}}>
                                <div className={classes.title}>
                                    <Typography variant="h6" style={{textAlign: "center"}}>
                                        Users with active shield
                                    </Typography>

                                    <Typography variant="h2" style={{textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center"}}>
                                        {this.state.statsLoaded ? this.state.stats.usersWithShield : <Skeleton width={150}/>}
                                    </Typography>
                                </div>
                            </div>
                        </Paper>
                    </Grid>

                    <Grid item xs={4}>
                        <Paper className="stats-root" elevation={0}>
                            <div style={{float: "left", padding: "15px 22px 15px 22px", width: "100%"}}>
                                <div className={classes.title}>
                                    <Typography variant="h6" style={{textAlign: "center"}}>
                                        Total Alliances
                                    </Typography>

                                    <Typography variant="h2" style={{textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center"}}>
                                        {this.state.statsLoaded ? this.state.stats.totalSquads : <Skeleton width={150}/>}
                                    </Typography>
                                </div>
                            </div>
                        </Paper>
                    </Grid>

                    <Grid item xs={4}>
                        <Paper className="stats-root" elevation={0}>
                            <div style={{float: "left", padding: "15px 22px 15px 22px", width: "100%"}}>
                                <div className={classes.title}>
                                    <Typography variant="h6" style={{textAlign: "center"}}>
                                        Wager Ruby Player
                                    </Typography>

                                    <Typography variant="h2" style={{textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center"}}>
                                        {this.state.statsLoaded ? this.state.stats.wagerPlayers : <Skeleton width={150}/>}
                                    </Typography>
                                </div>
                            </div>
                        </Paper>
                    </Grid>

                    <Grid item xs={4}>
                        <Paper className="stats-root" elevation={0}>
                            <div style={{float: "left", padding: "15px 22px 15px 22px", width: "100%"}}>
                                <div className={classes.title}>
                                    <Typography variant="h6" style={{textAlign: "center"}}>
                                        Banned Users
                                    </Typography>

                                    <Typography variant="h2" style={{textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center"}}>
                                        {this.state.statsLoaded ? this.state.stats.bannedUsers : <Skeleton width={150}/>}
                                    </Typography>
                                </div>
                            </div>
                        </Paper>
                    </Grid>

                    <Grid item xs={4}>
                        <Paper className="stats-root" elevation={0}>
                            <div style={{float: "left", padding: "15px 22px 15px 22px", width: "100%"}}>
                                <div className={classes.title}>
                                    <Typography variant="h6" style={{textAlign: "center"}}>
                                        Users in alliance
                                    </Typography>

                                    <Typography variant="h2" style={{textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center"}}>
                                        {this.state.statsLoaded ? this.state.stats.joinedAlliance : <Skeleton width={150}/>}
                                    </Typography>
                                </div>
                            </div>
                        </Paper>
                    </Grid>

                    <Grid item xs={showAlliances ? 6 : 12}>
                        <DashboardTopUsers/>
                    </Grid>

                    {showAlliances && <Grid item xs={6}>
                        <DashboardTopAlliances/>
                    </Grid>}

                    <Grid item xs={12}>
                        <Paper className="stats-root" elevation={0}>
                            <div style={{float: "left", padding: "15px 22px 15px 22px", width: "100%"}}>
                                <Typography variant="h6" style={{textAlign: "center"}}>
                                    Latest Payments
                                </Typography>

                                <Table className={this.props.classes.table}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{width: 50}}>Avatar</TableCell>
                                            <TableCell style={{width: 250}}>Username</TableCell>
                                            <TableCell>Product</TableCell>
                                            <TableCell align="right">Platform</TableCell>
                                            <TableCell align="right">Date</TableCell>
                                            <TableCell align="right">Price</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(this.state.tables.latestPayments || []).map(row => (
                                            <TableRow key={row.id}>
                                                <TableCell>
                                                    {Avatar(row.user)}
                                                </TableCell>
                                                <TableCell>
                                                    <NavLink to={`/players/${row.user.id}`}>
                                                        {row.user.name}
                                                    </NavLink>
                                                </TableCell>
                                                <TableCell>{this.getGoldPackData(row.product_id, "name")}</TableCell>
                                                <TableCell align="right">{row.platform}</TableCell>
                                                <TableCell
                                                    align="right">{Helpers.formatDateWithTime(row.created_at)}</TableCell>
                                                <TableCell
                                                    align="right">{`€${this.getGoldPackData(row.product_id, "price")}`}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </Paper>
                    </Grid>
                </Grid>
            </div>
        );
    }
}

const styles: any = theme => ({
    actions: {
        color: theme.palette.text.secondary,
        float: "right",
    },
    button: {
        margin: 8,
    },
});

export const Dashboard = withStyles(styles)(DashboardWrapped);
